import { z } from 'zod';
import { LvaCountRoundStatusSchema } from './lva-count.js';
import { LvaConditionSchema, LvaRiskTierSchema } from './lva.js';
// ============================================================
// Proactive constants — one source of truth shared by the server
// (lva-proactive.service) and any client. Both are keyed by risk tier
// (LOW/MEDIUM/HIGH). NO migration: pure constants (Phase-E D1/D3 locked).
// ============================================================
// Cycle-count interval by risk tier (days). A higher-risk asset is counted
// more often. Phase-E D3: constant by tier (per-company config deferred).
export const CYCLE_COUNT_INTERVAL_DAYS = {
    HIGH: 90,
    MEDIUM: 180,
    LOW: 365,
};
// Useful-life by risk tier (years) — drives the replacement forecast
// (acquiredDate + usefulLifeYears = replaceByDate). Phase-E D1: constant by
// tier (NO LvaCategory.usefulLifeYears migration).
export const USEFUL_LIFE_YEARS = {
    HIGH: 3,
    MEDIUM: 5,
    LOW: 7,
};
// ============================================================
// LvaProactiveItem — one IN_USE item enriched with the proactive signals.
// All derived flags/counters are computed server-side (read-only); the dates
// coerce from the canonical JSON strings. lastCountedAt is null when the item
// was never observed in a CLOSED round; daysSinceCount/daysToWarranty are null
// when their source date is null.
// ============================================================
export const LvaProactiveItemSchema = z.object({
    id: z.uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    branchName: z.string().nullable().optional(),
    categoryName: z.string().min(1),
    riskTier: LvaRiskTierSchema,
    acquiredDate: z.coerce.date(),
    condition: LvaConditionSchema,
    // Cycle-count signals.
    lastCountedAt: z.coerce.date().nullable(),
    cycleIntervalDays: z.number().int(),
    daysSinceCount: z.number().nullable(),
    cycleDue: z.boolean(),
    // Warranty signals.
    warrantyExpiry: z.coerce.date().nullable(),
    daysToWarranty: z.number().nullable(),
    warrantyDue: z.boolean(),
    // Replacement-forecast signals.
    usefulLifeYears: z.number().int(),
    replaceByDate: z.coerce.date(),
    replaceDue: z.boolean(),
    // Condition signal (POOR | DAMAGED).
    conditionFlag: z.boolean(),
});
// ============================================================
// LvaOverdueRound — a non-CLOSED count round whose dueDate is in the past.
// (dueDate is non-null here: only rounds that HAVE a past due date qualify.)
// ============================================================
export const LvaOverdueRoundSchema = z.object({
    id: z.uuid(),
    roundNo: z.string().min(1),
    period: z.string().min(1),
    dueDate: z.coerce.date(),
    status: LvaCountRoundStatusSchema,
    daysOverdue: z.number().int(),
});
// ============================================================
// LvaProactiveSummary — the proactive endpoint response: the enriched
// worklist items + overdue rounds + headline tallies for the KPI tiles.
// ============================================================
export const LvaProactiveSummarySchema = z.object({
    items: z.array(LvaProactiveItemSchema),
    overdueRounds: z.array(LvaOverdueRoundSchema),
    counts: z.object({
        cycleDue: z.number().int().nonnegative(),
        warrantyDue: z.number().int().nonnegative(),
        replaceDue: z.number().int().nonnegative(),
        conditionFlag: z.number().int().nonnegative(),
        overdueRounds: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=lva-proactive.js.map